// @flow

import authenticatedRequest from './authenticated-request'
import { addSuccessNotification, addErrorNotification } from './notification-manager'

import type { GetState, Dispatch, ReduxAction } from './index'
import type { NetError } from './authenticated-request'
import type { User } from './manage-users'

const dataUrl: string = '/api/campaigns'

// Action types
const CREATE_CAMPAIGN_PENDING: 'campaigns/CREATE_CAMPAIGN_PENDING' = 'campaigns/CREATE_CAMPAIGN_PENDING'
const CREATE_CAMPAIGN_SUCCESS: 'campaigns/CREATE_CAMPAIGN_SUCCESS' = 'campaigns/CREATE_CAMPAIGN_SUCCESS'
const CREATE_CAMPAIGN_ERROR: 'campaigns/CREATE_CAMPAIGN_ERROR' = 'campaigns/CREATE_CAMPAIGN_ERROR'

const DROP_CAMPAIGN_REQUEST: 'campaigns/DROP_CAMPAIGN_REQUEST' = 'campaigns/DROP_CAMPAIGN_REQUEST'
const DROP_CAMPAIGN_CANCEL: 'campaigns/DROP_CAMPAIGN_CANCEL' = 'campaigns/DROP_CAMPAIGN_CANCEL'
const DROP_CAMPAIGN_PENDING: 'campaigns/DROP_CAMPAIGN_PENDING' = 'campaigns/DROP_CAMPAIGN_PENDING'
const DROP_CAMPAIGN_SUCCESS: 'campaigns/DROP_CAMPAIGN_SUCCESS' = 'campaigns/DROP_CAMPAIGN_SUCCESS'
const DROP_CAMPAIGN_ERROR: 'campaigns/DROP_CAMPAIGN_ERROR' = 'campaigns/DROP_CAMPAIGN_ERROR'

const CREATE_WAVE_PENDING: 'campaigns/CREATE_WAVE_PENDING' = 'campaigns/CREATE_WAVE_PENDING'
const CREATE_WAVE_SUCCESS: 'campaigns/CREATE_WAVE_SUCCESS' = 'campaigns/CREATE_WAVE_SUCCESS'
const CREATE_WAVE_ERROR: 'campaigns/CREATE_WAVE_ERROR' = 'campaigns/CREATE_WAVE_ERROR'

const DROP_WAVE_PENDING: 'campaigns/DROP_WAVE_PENDING' = 'campaigns/DROP_WAVE_PENDING'
const DROP_WAVE_SUCCESS: 'campaigns/DROP_WAVE_SUCCESS' = 'campaigns/DROP_WAVE_SUCCESS'
const DROP_WAVE_ERROR: 'campaigns/DROP_WAVE_ERROR' = 'campaigns/DROP_WAVE_ERROR'

const ADD_USER_PENDING: 'campaigns/ADD_USER_PENDING' = 'campaigns/ADD_USER_PENDING'
const ADD_USER_SUCCESS: 'campaigns/ADD_USER_SUCCESS' = 'campaigns/ADD_USER_SUCCESS'
const ADD_USER_ERROR: 'campaigns/ADD_USER_ERROR' = 'campaigns/ADD_USER_ERROR'

const REMOVE_USER_PENDING: 'campaigns/REMOVE_USER_PENDING' = 'campaigns/REMOVE_USER_PENDING'
const REMOVE_USER_SUCCESS: 'campaigns/REMOVE_USER_SUCCESS' = 'campaigns/REMOVE_USER_SUCCESS'
const REMOVE_USER_ERROR: 'campaigns/REMOVE_USER_ERROR' = 'campaigns/REMOVE_USER_ERROR'

const FETCH_PENDING: 'campaigns/FETCH_PENDING' = 'campaigns/FETCH_PENDING'
const FETCH_SUCCESS: 'campaigns/FETCH_SUCCESS' = 'campaigns/FETCH_SUCCESS'
const FETCH_ERROR: 'campaigns/FETCH_ERROR' = 'campaigns/FETCH_ERROR'

const SET_CAMPAIGN_STATUS_PENDING: 'campaigns/SET_CAMPAIGN_STATUS_PENDING' = 'campaigns/SET_CAMPAIGN_STATUS_PENDING'
const SET_CAMPAIGN_STATUS_SUCCESS: 'campaigns/SET_CAMPAIGN_STATUS_SUCCESS' = 'campaigns/SET_CAMPAIGN_STATUS_SUCCESS'
const SET_CAMPAIGN_STATUS_ERROR: 'campaigns/SET_CAMPAIGN_STATUS_ERROR' = 'campaigns/SET_CAMPAIGN_STATUS_ERROR'

const SET_CAMPAIGN_FILTER: 'campaigns/SET_CAMPAIGN_FILTER' = 'campaigns/SET_CAMPAIGN_FILTER'
const VIEW_CHANGE: 'campaigns/VIEW_CHANGE' = 'campaigns/VIEW_CHANGE'

export type Wave = {|
  name: string,
  created_at: number,
|}

type ChannelSummary = {|
  visits: number,
  rate: number,
|}

export type WaveSummary = {|
  num_waves: number,
  last_wave: string,
  visits_last_wave: number,
|}

export type CardSummary = {|
  WEB: ChannelSummary,
  F2F: ChannelSummary,
  PHONE: ChannelSummary,
  total: ChannelSummary,
  wave: WaveSummary,
|}

export type Campaign = {|
  name: string,
  created_at: number,
  active: boolean,
  users: Array<User>,
  waves: Array<Wave>,
  summary: CardSummary,
|}

type ViewType = 'list' | 'grid' | 'block'

type ACTION_CREATE_CAMPAIGN_PENDING = {| type: typeof CREATE_CAMPAIGN_PENDING |}
type ACTION_CREATE_CAMPAIGN_SUCCESS = {| type: typeof CREATE_CAMPAIGN_SUCCESS, payload: Object |}
type ACTION_CREATE_CAMPAIGN_ERROR = {| type: typeof CREATE_CAMPAIGN_ERROR, payload: NetError |}

type ACTION_DROP_CAMPAIGN_REQUEST = {| type: typeof DROP_CAMPAIGN_REQUEST, payload: string |}
type ACTION_DROP_CAMPAIGN_CANCEL = {| type: typeof DROP_CAMPAIGN_CANCEL |}
type ACTION_DROP_CAMPAIGN_PENDING = {| type: typeof DROP_CAMPAIGN_PENDING |}
type ACTION_DROP_CAMPAIGN_SUCCESS = {| type: typeof DROP_CAMPAIGN_SUCCESS |}
type ACTION_DROP_CAMPAIGN_ERROR = {| type: typeof DROP_CAMPAIGN_ERROR, payload: NetError |}

type WavePayload = {| campaignName: string, waveName: string |}
type ACTION_CREATE_WAVE_PENDING = {| type: typeof CREATE_WAVE_PENDING, payload: WavePayload |}
type ACTION_CREATE_WAVE_SUCCESS = {| type: typeof CREATE_WAVE_SUCCESS, payload: Campaign |}
type ACTION_CREATE_WAVE_ERROR = {| type: typeof CREATE_WAVE_ERROR, payload: NetError |}
type ACTION_DROP_WAVE_PENDING = {| type: typeof DROP_WAVE_PENDING, payload: WavePayload |}
type ACTION_DROP_WAVE_SUCCESS = {| type: typeof DROP_WAVE_SUCCESS, payload: Campaign |}
type ACTION_DROP_WAVE_ERROR = {| type: typeof DROP_WAVE_ERROR, payload: NetError |}

type UserPayload = {| campaignName: string, username: string |}
type ACTION_ADD_USER_PENDING = {| type: typeof ADD_USER_PENDING, payload: UserPayload |}
type ACTION_ADD_USER_SUCCESS = {| type: typeof ADD_USER_SUCCESS, payload: Campaign |}
type ACTION_ADD_USER_ERROR = {| type: typeof ADD_USER_ERROR, payload: NetError |}

type ACTION_REMOVE_USER_PENDING = {| type: typeof REMOVE_USER_PENDING, payload: UserPayload |}
type ACTION_REMOVE_USER_SUCCESS = {| type: typeof REMOVE_USER_SUCCESS, payload: Campaign |}
type ACTION_REMOVE_USER_ERROR = {| type: typeof REMOVE_USER_ERROR, payload: NetError |}

type ACTION_FETCH_PENDING = {| type: typeof FETCH_PENDING |}
type ACTION_FETCH_SUCCESS = {| type: typeof FETCH_SUCCESS, payload: Array<Campaign> |}
type ACTION_FETCH_ERROR = {| type: typeof FETCH_ERROR, payload: NetError |}

type ACTION_SET_CAMPAIGN_STATUS_PENDING = {|
  type: typeof SET_CAMPAIGN_STATUS_PENDING,
  payload: {| campaignName: string |},
|}
type ACTION_SET_CAMPAIGN_STATUS_SUCCESS = {|
  type: typeof SET_CAMPAIGN_STATUS_SUCCESS,
  payload: {| campaignName: string, isActive: boolean |},
|}
type ACTION_SET_CAMPAIGN_STATUS_ERROR = {| type: typeof SET_CAMPAIGN_STATUS_ERROR, payload: NetError |}

type ACTION_SET_CAMPAIGN_FILTER = {| type: typeof SET_CAMPAIGN_FILTER, payload: boolean |}
type ACTION_VIEW_CHANGE = {| type: typeof VIEW_CHANGE, payload: ViewType |}

export type CampaignsAction =
  | ACTION_CREATE_CAMPAIGN_PENDING
  | ACTION_CREATE_CAMPAIGN_SUCCESS
  | ACTION_CREATE_CAMPAIGN_ERROR
  | ACTION_DROP_CAMPAIGN_REQUEST
  | ACTION_DROP_CAMPAIGN_CANCEL
  | ACTION_DROP_CAMPAIGN_PENDING
  | ACTION_DROP_CAMPAIGN_SUCCESS
  | ACTION_DROP_CAMPAIGN_ERROR
  | ACTION_CREATE_WAVE_PENDING
  | ACTION_CREATE_WAVE_SUCCESS
  | ACTION_CREATE_WAVE_ERROR
  | ACTION_DROP_WAVE_PENDING
  | ACTION_DROP_WAVE_SUCCESS
  | ACTION_DROP_WAVE_ERROR
  | ACTION_ADD_USER_PENDING
  | ACTION_ADD_USER_SUCCESS
  | ACTION_ADD_USER_ERROR
  | ACTION_REMOVE_USER_PENDING
  | ACTION_REMOVE_USER_SUCCESS
  | ACTION_REMOVE_USER_ERROR
  | ACTION_FETCH_PENDING
  | ACTION_FETCH_SUCCESS
  | ACTION_FETCH_ERROR
  | ACTION_SET_CAMPAIGN_STATUS_PENDING
  | ACTION_SET_CAMPAIGN_STATUS_SUCCESS
  | ACTION_SET_CAMPAIGN_STATUS_ERROR
  | ACTION_SET_CAMPAIGN_FILTER
  | ACTION_VIEW_CHANGE

const statuses = {
  NEW: 'NEW',
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
}

type Status = 'NEW' | 'PENDING' | 'SUCCESS' | 'ERROR'
type CampaignsState_ = {|
  status: Status,
  error: string,
  data: Array<Campaign>,
  viewType: ViewType,
  filterActiveCampaigns: boolean,
  dropCampaignPending: string,
  editCampaignPending: string,
  message: string,
|}
export type CampaignsState = $Shape<CampaignsState_>

// Initial state
const initialState = {
  status: statuses.NEW,
  error: '',
  data: [],
  viewType: 'grid',
  filterActiveCampaigns: false,
  dropCampaignPending: '',
  editCampaignPending: '',
  message: '',
}

export const reducer = (state: CampaignsState = initialState, action: ReduxAction): CampaignsState => {
  switch (action.type) {
    case FETCH_PENDING:
    case DROP_CAMPAIGN_PENDING:
      return { ...state, status: statuses.PENDING, message: undefined }

    case CREATE_WAVE_PENDING:
    case DROP_WAVE_PENDING:
    case ADD_USER_PENDING:
    case REMOVE_USER_PENDING:
    case SET_CAMPAIGN_STATUS_PENDING:
      return { ...state, editCampaignPending: action.payload.campaignName }

    case CREATE_WAVE_SUCCESS: {
      let a = action
      let data = [...state.data].map(e => {
        if (e.name === a.payload.name) {
          return a.payload
        }
        return e
      })
      return { ...state, editCampaignPending: '', data: data }
    }

    case DROP_WAVE_SUCCESS: {
      let a = action
      let data = [...state.data].map(e => {
        if (e.name === a.payload.name) {
          return a.payload
        }
        return e
      })
      return { ...state, editCampaignPending: '', data: data }
    }

    case ADD_USER_SUCCESS: {
      let a = action
      let data = [...state.data].map(e => {
        if (e.name === a.payload.name) {
          return a.payload
        }
        return e
      })
      return { ...state, editCampaignPending: '', data: data }
    }

    case REMOVE_USER_SUCCESS: {
      let a = action
      let data = [...state.data].map(e => {
        if (e.name === a.payload.name) {
          return a.payload
        }
        return e
      })
      return { ...state, editCampaignPending: '', data: data }
    }

    case SET_CAMPAIGN_STATUS_SUCCESS: {
      let a = action
      let data = [...state.data].map(e => {
        if (e.name === a.payload.campaignName) {
          e.active = a.payload.isActive
        }
        return e
      })
      return { ...state, editCampaignPending: '', data: data }
    }

    case CREATE_WAVE_ERROR:
    case DROP_WAVE_ERROR:
    case ADD_USER_ERROR:
    case REMOVE_USER_ERROR:
    case SET_CAMPAIGN_STATUS_ERROR:
      return {
        ...state,
        editCampaignPending: '',
        status: statuses.ERROR,
        message: action.payload.response.data.message,
      }

    case FETCH_SUCCESS:
      return { ...state, data: action.payload, status: statuses.SUCCESS, message: undefined }

    case CREATE_CAMPAIGN_SUCCESS:
      return state

    case DROP_CAMPAIGN_SUCCESS:
      return { ...state, dropCampaignPending: '' }

    case FETCH_ERROR:
    case CREATE_CAMPAIGN_ERROR:
      return { ...state, status: statuses.ERROR, message: action.payload.response.data.message }

    case DROP_CAMPAIGN_ERROR:
      return {
        ...state,
        status: statuses.ERROR,
        message: action.payload.response.data.message,
        dropCampaignPending: '',
      }

    case DROP_CAMPAIGN_REQUEST:
      return { ...state, dropCampaignPending: action.payload }

    case DROP_CAMPAIGN_CANCEL:
      return { ...state, dropCampaignPending: '' }

    case VIEW_CHANGE:
      return { ...state, viewType: action.payload }

    case SET_CAMPAIGN_FILTER:
      return { ...state, filterActiveCampaigns: action.payload }

    default:
      return state
  }
}

// Action creators
export const getCampaigns = () => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: FETCH_PENDING })
  try {
    let response = await authenticatedRequest.get(dataUrl)
    dispatch({ type: FETCH_SUCCESS, payload: response.data.data })
  } catch (err) {
    dispatch({ type: FETCH_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const createCampaign = (campaignName: string) => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: CREATE_CAMPAIGN_PENDING })
  try {
    let response = await authenticatedRequest.post(dataUrl + '/new', { campaignName })
    dispatch({ type: CREATE_CAMPAIGN_SUCCESS, payload: response.data.data })
    dispatch(await getCampaigns())
    dispatch(addSuccessNotification(response.data.message))
  } catch (err) {
    dispatch({ type: CREATE_CAMPAIGN_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const dropCampaignRequest = (campaignName: string): ACTION_DROP_CAMPAIGN_REQUEST => ({
  type: DROP_CAMPAIGN_REQUEST,
  payload: campaignName,
})
export const dropCampaignCancel = (): ACTION_DROP_CAMPAIGN_CANCEL => ({ type: DROP_CAMPAIGN_CANCEL })
export const dropCampaign = (campaignName: string) => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: DROP_CAMPAIGN_PENDING })
  try {
    let response = await authenticatedRequest.delete(dataUrl + '/' + encodeURIComponent(campaignName) + '/delete')
    dispatch({ type: DROP_CAMPAIGN_SUCCESS })
    dispatch(await getCampaigns())
    dispatch(addSuccessNotification(response.data.message))
  } catch (err) {
    dispatch({ type: DROP_CAMPAIGN_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const createWave = (campaignName: string, waveName: string) => async (
  dispatch: Dispatch,
  getState: GetState
) => {
  dispatch({ type: CREATE_WAVE_PENDING, payload: { campaignName, waveName } })
  try {
    let response = await authenticatedRequest.post(dataUrl + '/' + encodeURIComponent(campaignName) + '/waves/new', {
      waveName,
    })
    dispatch({ type: CREATE_WAVE_SUCCESS, payload: response.data.data.campaign })
    dispatch(addSuccessNotification(response.data.message))
  } catch (err) {
    dispatch({ type: CREATE_WAVE_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const dropWave = (campaignName: string, waveName: string) => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: DROP_WAVE_PENDING, payload: { campaignName, waveName } })
  try {
    let response = await authenticatedRequest.delete(
      dataUrl + '/' + encodeURIComponent(campaignName) + '/waves/' + encodeURIComponent(waveName) + '/delete'
    )
    dispatch({ type: DROP_WAVE_SUCCESS, payload: response.data.data.campaign })
    dispatch(addSuccessNotification(response.data.message))
  } catch (err) {
    dispatch({ type: DROP_WAVE_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const addUser = (campaignName: string, username: string) => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: ADD_USER_PENDING, payload: { campaignName, username } })
  try {
    let response = await authenticatedRequest.post(dataUrl + '/' + encodeURIComponent(campaignName) + '/users/add', {
      username,
    })
    dispatch({ type: ADD_USER_SUCCESS, payload: response.data.data.campaign })
    dispatch(addSuccessNotification(response.data.message))
  } catch (err) {
    dispatch({ type: ADD_USER_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const removeUser = (campaignName: string, username: string) => async (
  dispatch: Dispatch,
  getState: GetState
) => {
  dispatch({ type: REMOVE_USER_PENDING, payload: { campaignName, username } })
  try {
    let response = await authenticatedRequest.delete(
      dataUrl + '/' + encodeURIComponent(campaignName) + '/users/' + encodeURIComponent(username) + '/remove'
    )
    dispatch({ type: REMOVE_USER_SUCCESS, payload: response.data.data.campaign })
    dispatch(addSuccessNotification(response.data.message))
  } catch (err) {
    dispatch({ type: REMOVE_USER_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const setCampaignStatus = (campaignName: string, isActive: boolean) => async (
  dispatch: Dispatch,
  getState: GetState
) => {
  dispatch({ type: SET_CAMPAIGN_STATUS_PENDING, payload: { campaignName } })
  try {
    let response = await authenticatedRequest.post(dataUrl + '/' + encodeURIComponent(campaignName) + '/setstatus', {
      active: isActive,
    })
    dispatch({ type: SET_CAMPAIGN_STATUS_SUCCESS, payload: { campaignName, isActive } })
    dispatch(addSuccessNotification(response.data.message))
  } catch (err) {
    dispatch({ type: SET_CAMPAIGN_STATUS_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const changeView = (viewType: ViewType): ACTION_VIEW_CHANGE => ({ type: VIEW_CHANGE, payload: viewType })
export const setActiveFilter = (activesOnly: boolean): ACTION_SET_CAMPAIGN_FILTER => ({
  type: SET_CAMPAIGN_FILTER,
  payload: activesOnly,
})

export default reducer



// WEBPACK FOOTER //
// ./src/store/campaigns.js